/*
 * Skeleton: shimmering placeholder lines (styles/motion.css .motion-skeleton) where text will land, so a
 * card keeps its shape while a read is in flight (owner 2026-10-13: loading is a shape, not a spinner).
 * `lines` is how many; `width` fixes every line's width (a percentage or a CSS length), otherwise the
 * lines vary between 55% and 95% like the DataTable's. Decorative: hidden from assistive technology;
 * the container that waits should carry aria-busy (SkeletonCard does).
 */
type SkeletonProps = {
    className?: string;
    lines?: number;
    width?: string | number;
};

/** A deterministic width per line, so server and client render the same markup. */
const lineWidth = (index: number) => `${55 + ((index * 17) % 40)}%`;

const Skeleton = ({ className, lines = 3, width }: SkeletonProps) => (
    <div className={`skeleton ${className || ""}`} aria-hidden="true">
        {Array.from({ length: lines }, (_, index) => (
            <span className="motion-skeleton" style={{ width: width ?? lineWidth(index) }} key={index} />
        ))}
    </div>
);

export default Skeleton;

/*
 * SkeletonCard: a card standing in for one that has not loaded yet: a title line, then `rows` lines. It is
 * busy and says so (`label`, "Loading" by default, for apps that translate).
 */
type SkeletonCardProps = {
    className?: string;
    rows?: number;
    label?: string;
};

export const SkeletonCard = ({ className, rows = 3, label = "Loading" }: SkeletonCardProps) => (
    <div className={`card skeleton ${className || ""}`} role="status" aria-busy="true">
        <span className="sr-only">{label}</span>
        <span className="motion-skeleton skeleton__title" style={{ width: "40%" }} aria-hidden="true" />
        <Skeleton lines={rows} />
    </div>
);
